/*****************************************************************
 *  CUSTOMER BUTLER - THE PARTS OF ZOHO'S RECORD PAGE (HR look)    *
 *  File: apps/web/src/pages/customer/ZohoRecordParts.js           *
 *  What it does: the pieces a customer or property record page is *
 *  built from, in Zoho's arrangement and HR Butler's look:        *
 *    RelatedRail      the left "Related List" jump list + counts, *
 *                     with the small summary block under it       *
 *                     (a folded "Related" card on a phone)        *
 *    TitleRow         back arrow, the name, "Add Tags", buttons   *
 *    ViewSwitch       Overview / Timeline + "Last update: ..."    *
 *    StageStrip       Zoho's pipeline strip - a labelled hook,    *
 *                     empty for Contacts and Properties           *
 *    SummaryCard      Zoho's business-card fields, label | value  *
 *    SidePanels       the row of panels (Person / Latest thing /  *
 *                     Next action), Zoho's three under the card   *
 *    DetailsBlock     Hide Details / Show Details, then every     *
 *                     section, two columns, label | value         *
 *    describeLastUpdate  the words after "Last update:"           *
 *  Reads: what the page hands it.  Writes: only through the       *
 *  field editor the details already used (pencils per level), and *
 *  the per-browser "details hidden" choice.                       *
 *****************************************************************/

/* WHY (2026-10-15, James via Fable): "you got that part right" (the HR
   look); "now the record page STRUCTURE must mimic Zoho's record page as
   his team sees it, in that look." Read from his Zoho Deals/Jobs screenshots
   of 02:44, 02:47 and 02:48. Two columns in the details, label left and
   value right, like Zoho (this also settles the one-vs-two column
   question: two). */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - SMALL WORDS (last update)                          *
 *  SECTION 2 - THE RELATED-LIST RAIL                              *
 *  SECTION 3 - THE TITLE ROW AND THE TAGS LINE                    *
 *  SECTION 4 - OVERVIEW / TIMELINE, AND THE STAGE STRIP HOOK      *
 *  SECTION 5 - THE SUMMARY CARD AND THE SIDE PANELS               *
 *  SECTION 6 - HIDE / SHOW DETAILS                                *
 *****************************************************************/

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { StatusChip } from './customerParts';
import { formatDay } from './customerDates';
import { SectionFacts, sentenceCaseTitle } from './RecordSections';
import { useCustomerAccessView } from './customerAccessView';
import { blockIdFor } from './ZohoRelatedBlocks';

/*****************************************************************
 *  SECTION 1 - SMALL WORDS                                        *
 *****************************************************************/

const ONE_DAY_MS = 24 * 60 * 60 * 1000; // one day, in milliseconds
const DAYS_SHOWN_AS_AGO = 7;            // "3 days ago" up to a week, then the date

/* ---- startOfDay(moment) -- midnight of that day, on this device ---- */
function startOfDay(moment) {
  const dayStart = new Date(moment);

  dayStart.setHours(0, 0, 0, 0);

  return dayStart.getTime();
}

/* ---- describeLastUpdate(whenText, now) ---------------------------
   In: an ISO time; now (a Date, for tests).
   Out: "Today", "Yesterday", "3 days ago", or the date. '' if none. */
export function describeLastUpdate(whenText, now) {
  if (!whenText) {
    return '';
  }

  const thenDay = startOfDay(new Date(whenText));
  const todayDay = startOfDay(now || new Date());
  const daysBetween = Math.round((todayDay - thenDay) / ONE_DAY_MS);

  if (daysBetween <= 0) {
    return 'Today';
  }
  if (daysBetween === 1) {
    return 'Yesterday';
  }
  if (daysBetween < DAYS_SHOWN_AS_AGO) {
    return `${daysBetween} days ago`;
  }

  return formatDay(whenText);
}

/*****************************************************************
 *  SECTION 2 - THE RELATED-LIST RAIL                              *
 *  Zoho's left column: "Related List", every list in Zoho's order *
 *  with its count, each a jump to its block; then a small summary *
 *  (Zoho's "Sales Summary" / "Links" place). On a phone the same  *
 *  links sit in a folded "Related" card at the top.               *
 *****************************************************************/

/* ---- RailLinks({ rail, countFor, onJump }) -- the list of jumps ---- */
function RailLinks({ rail, countFor, onJump }) {
  return (
    <ul className="cb-zr-rail-list">
      {rail.map((railEntry) => {
        const listCount = countFor(railEntry);
        const isConnected = Boolean(railEntry.block);

        return (
          <li key={railEntry.label}>
            <a
              href={`#${blockIdFor(railEntry)}`}
              className={isConnected ? 'cb-zr-rail-link' : 'cb-zr-rail-link is-muted'}
              onClick={(clickEvent) => {
                clickEvent.preventDefault();
                onJump(railEntry);
              }}
            >
              <span>{railEntry.label}</span>
              {typeof listCount === 'number' && listCount > 0 ? <span className="cb-zr-count">{listCount}</span> : null}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/* ---- RailSummary({ title, facts }) -- the small block under the rail ----
   facts: [{ label, value }]. Butler's honest record facts.        */
function RailSummary({ title, facts }) {
  return (
    <div className="cb-zr-rail-summary">
      <h2 className="cb-zr-rail-head">{title}</h2>
      {facts.map((oneFact) => (
        <p key={oneFact.label} className="cb-zr-rail-fact">
          <span>{oneFact.label}:</span> {oneFact.value || '—'}
        </p>
      ))}
    </div>
  );
}

/* ---- RelatedRail({ rail, countFor, onJump, summaryTitle, summaryFacts }) ----
   The desktop column. Out: <aside>. Hidden on a phone (CSS).    */
export function RelatedRail({ rail, countFor, onJump, summaryTitle, summaryFacts }) {
  return (
    <aside className="cb-zr-rail" aria-label="Related List">
      <h2 className="cb-zr-rail-head">Related List</h2>
      <RailLinks rail={rail} countFor={countFor} onJump={onJump} />
      <RailSummary title={summaryTitle} facts={summaryFacts} />
    </aside>
  );
}

/* ---- PhoneRelatedCard({ rail, countFor, onJump }) -- the phone's folded rail ---- */
export function PhoneRelatedCard({ rail, countFor, onJump }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <details className="cb-hr-sec cb-zr-phone-rail" open={isOpen} onToggle={(toggleEvent) => setIsOpen(toggleEvent.currentTarget.open)}>
      <summary>
        <Icon name="file" size="button" />
        <span className="cb-hr-sec-title">Related</span>
        <span className="cb-hr-sec-caret"><Icon name="arrowright" /></span>
      </summary>
      {isOpen ? (
        <div className="cb-hr-sec-body">
          <RailLinks
            rail={rail}
            countFor={countFor}
            onJump={(railEntry) => {
              setIsOpen(false);
              onJump(railEntry);
            }}
          />
        </div>
      ) : null}
    </details>
  );
}

/*****************************************************************
 *  SECTION 3 - THE TITLE ROW AND THE TAGS LINE                    *
 *****************************************************************/

/* ---- findScreenField(layout, fieldKey) -- one field of the layout, or null ---- */
export function findScreenField(layout, fieldKey) {
  for (const oneSection of layout.sections) {
    const foundField = oneSection.fields.find((oneField) => oneField.key === fieldKey);

    if (foundField) {
      return foundField;
    }
  }

  return null;
}

/* ---- TagsLine({ record, layout, onSaveField }) -------------------
   Zoho's "Add Tags" line under the name: the tags as chips, and
   (Office and up) "Add Tags" opens the Tag field's own editor.   */
function TagsLine({ record, layout, onSaveField }) {
  const [isEditing, setIsEditing] = useState(false);
  const accessView = useCustomerAccessView();
  const tagsField = findScreenField(layout, 'tags');
  const tags = Array.isArray(record.tags) ? record.tags : [];

  if (!tagsField) {
    return null;
  }

  return (
    <div className="cb-zr-tags">
      {tags.map((oneTag) => <StatusChip key={oneTag} tone="neutral" icon="tag" word={oneTag} />)}
      {accessView.mayWrite ? (
        <button type="button" className="cb-link-btn" onClick={() => setIsEditing((editingNow) => !editingNow)}>
          <Icon name="tag" /> {isEditing ? 'Done with tags' : 'Add Tags'}
        </button>
      ) : null}
      {isEditing ? (
        <div className="cb-zr-tags-edit">
          <SectionFacts record={record} section={{ title: 'Tags', fields: [tagsField] }} onSaveField={onSaveField} />
        </div>
      ) : null}
    </div>
  );
}

/* ---- TitleRow({ backTo, backWords, title, account, under, record, layout, onSaveField, actions }) ----
   Zoho's title row: back arrow, the letter tile, the name and
   " - <account>", the Add Tags line; the buttons on the right.  */
export function TitleRow({ backTo, backWords, title, account, under, record, layout, onSaveField, actions }) {
  const firstLetter = String(title || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="cb-zr-titlerow">
      <Link className="cb-zr-backarrow" to={backTo} aria-label={backWords} title={backWords}>
        <Icon name="arrowleft" size="button" />
      </Link>
      <span className="cb-zr-letter" aria-hidden="true">{firstLetter}</span>
      <div className="cb-zr-titlewords">
        <h1 className="cb-zr-title">
          {title}
          {account ? <span className="cb-zr-account"> - {account}</span> : null}
        </h1>
        {under ? <div className="cb-hr-person-mail cb-zr-under">{under}</div> : null}
        <TagsLine record={record} layout={layout} onSaveField={onSaveField} />
      </div>
      {actions ? <div className="cb-zr-title-acts">{actions}</div> : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 - OVERVIEW / TIMELINE, AND THE STAGE STRIP HOOK      *
 *****************************************************************/

export const VIEW_OVERVIEW = 'overview';
export const VIEW_TIMELINE = 'timeline';

/* ---- ViewSwitch({ view, onChoose, lastUpdateWords }) -------------
   HR's chips for Zoho's two views, "Last update" on the right.   */
export function ViewSwitch({ view, onChoose, lastUpdateWords }) {
  const choices = [
    { key: VIEW_OVERVIEW, words: 'Overview', icon: 'file' },
    { key: VIEW_TIMELINE, words: 'Timeline', icon: 'clock' },
  ];

  return (
    <div className="cb-zr-viewrow">
      <div className="cb-hr-tabs" role="tablist" aria-label="Record view">
        {choices.map((oneChoice) => (
          <button
            key={oneChoice.key}
            type="button"
            role="tab"
            className="cb-hr-tab"
            aria-selected={view === oneChoice.key ? 'true' : 'false'}
            onClick={() => onChoose(oneChoice.key)}
          >
            <Icon name={oneChoice.icon} />
            {oneChoice.words}
          </button>
        ))}
      </div>
      {lastUpdateWords ? (
        <span className="cb-zr-lastupdate"><Icon name="clock" /> Last update: {lastUpdateWords}</span>
      ) : null}
    </div>
  );
}

/* ---- StageStrip({ pipeline }) ------------------------------------
   THE HOOK FOR DEALS/JOBS. Zoho draws a stage strip across the top
   of a record that has a pipeline (Deals/Jobs: Estimate Requested ...
   Confirmed/Scheduled ...). Contacts and Properties have none, so it
   draws nothing today. When Deals/Jobs come over, pass the pipeline
   ({ stages: [{ key, label }], currentKey, since }) and draw it here.
   WHY (2026-10-15, James via Fable): "keep the hook labeled".     */
export function StageStrip({ pipeline }) {
  if (!pipeline) {
    return null;
  }

  return null; // Deals/Jobs: not connected yet - nothing is drawn that is not real
}

/*****************************************************************
 *  SECTION 5 - THE SUMMARY CARD AND THE SIDE PANELS               *
 *  WHY (2026-10-15, Zoho tour frame 043, a Contact record): under *
 *  the summary card Zoho shows three panels side by side -        *
 *  "Deals/Jobs" (latest job + stage chip), "Next Action" (dated   *
 *  tasks) and "Finance Details". Butler's honest three: the       *
 *  latest Property (+ its alerts chip), the Next action, and the  *
 *  latest Report (from the Report Engine's door).                 *
 *****************************************************************/

/* ---- SummaryCard({ record, layout, onSaveField }) ----------------
   Zoho's "business card": layout.businessCard's fields, label left,
   value right, the same pencils as the details.                 */
export function SummaryCard({ record, layout, onSaveField }) {
  const cardFields = (layout.businessCard || [])
    .map((fieldKey) => findScreenField(layout, fieldKey))
    .filter(Boolean);

  if (cardFields.length === 0) {
    return null;
  }

  return (
    <div className="cb-zr-card cb-zr-labelleft cb-zr-summary">
      <SectionFacts record={record} section={{ title: 'Summary', fields: cardFields }} onSaveField={onSaveField} />
    </div>
  );
}

/* ---- Panel({ title, children }) -- one side panel ---- */
export function Panel({ title, children }) {
  return (
    <div className="cb-zr-panel">
      <h2 className="cb-zr-panel-title">{title}</h2>
      {children}
    </div>
  );
}

/* ---- PersonPanel({ title, person }) ------------------------------
   person: { name, company, email, phone, linkTo } or null.        */
export function PersonPanel({ title, person }) {
  return (
    <Panel title={title}>
      {!person ? <p className="cb-zr-empty">No contact on file.</p> : (
        <div className="cb-zr-person">
          <span className="cb-hr-avatar" aria-hidden="true"><Icon name="user" /></span>
          <div className="cb-zr-person-lines">
            {person.linkTo ? <Link className="cb-zr-person-name" to={person.linkTo}>{person.name}</Link> : <span className="cb-zr-person-name">{person.name}</span>}
            {person.company ? <span className="cb-zr-person-line">at {person.company}</span> : null}
            {person.email ? <a className="cb-zr-person-line" href={`mailto:${person.email}`}><Icon name="link" /> {person.email}</a> : null}
            {person.phone ? <a className="cb-zr-person-line" href={`tel:${person.phone}`}><Icon name="phone" /> {person.phone}</a> : null}
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ---- LatestThingPanel({ title, name, linkTo, chip, emptyWords }) --
   Zoho's "Deals/Jobs" panel shape: the latest one's name, a chip. */
export function LatestThingPanel({ title, name, linkTo, href, chip, emptyWords }) {
  let nameNode = <span className="cb-zr-person-name">{name}</span>;

  if (linkTo) {
    nameNode = <Link className="cb-zr-person-name" to={linkTo}>{name}</Link>;
  } else if (href) {
    nameNode = <a className="cb-zr-person-name" href={href} target="_blank" rel="noopener noreferrer">{name}</a>;
  }

  return (
    <Panel title={title}>
      {!name ? <p className="cb-zr-empty">{emptyWords}</p> : (
        <div className="cb-zr-latest">
          {nameNode}
          {chip ? <StatusChip tone={chip.tone} icon={chip.icon} word={chip.word} /> : null}
        </div>
      )}
    </Panel>
  );
}

const MOST_NEXT_ACTIONS = 5; // Zoho's panel shows a short dated list

/* ---- NextActionPanel({ sorted }) ---------------------------------
   The open tasks (soonest first), then the booked visits, each with
   its date as a tag - Zoho's red "OCT 2" tags, as a chip with a
   word and an icon. An honest line when nothing is planned.      */
export function NextActionPanel({ sorted }) {
  if (!sorted) {
    return <Panel title="Next action"><p className="cb-zr-empty">Loading</p></Panel>;
  }

  const planned = [
    ...sorted.open.tasks.map((oneTask) => ({ id: `t-${oneTask.id}`, words: oneTask.title, when: oneTask.dueAt, icon: 'check' })),
    ...sorted.open.meetings.map((oneEntry) => ({ id: `m-${oneEntry.id}`, words: oneEntry.summary, when: oneEntry.occurredAt, icon: 'calendar' })),
  ].slice(0, MOST_NEXT_ACTIONS);

  return (
    <Panel title="Next action">
      {planned.length === 0 ? <p className="cb-zr-empty">Nothing planned yet. No open task and no visit booked.</p> : (
        <ul className="cb-zr-next-list">
          {planned.map((onePlan) => (
            <li key={onePlan.id} className="cb-zr-next">
              <StatusChip tone="warn" icon={onePlan.icon} word={onePlan.when ? formatDay(onePlan.when) : 'No date'} />
              <span className="cb-zr-next-words">{onePlan.words}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ---- SidePanels({ children }) -- the row of panels under the summary ---- */
export function SidePanels({ children }) {
  return <div className="cb-zr-card cb-zr-panels">{children}</div>;
}

/*****************************************************************
 *  SECTION 6 - HIDE / SHOW DETAILS                                *
 *  Every section in Zoho's order, a titled block, two columns,    *
 *  label left and value right; the choice is kept per browser.    *
 *****************************************************************/

const DETAILS_HIDDEN_KEY = 'cb-details-hidden'; // per-viewer convenience

/* ---- readDetailsHidden() -- false (shown) unless this browser hid them ---- */
function readDetailsHidden() {
  try {
    return window.localStorage.getItem(DETAILS_HIDDEN_KEY) === 'yes';
  } catch (storageProblem) {
    return false;
  }
}

/* ---- DetailsBlock({ record, sections, onSaveField }) ---- */
export function DetailsBlock({ record, sections, onSaveField }) {
  const [isHidden, setIsHidden] = useState(readDetailsHidden);

  /* ---- flipDetails() -- hide or show, and remember it here ---- */
  function flipDetails() {
    const nextHidden = !isHidden;

    try {
      window.localStorage.setItem(DETAILS_HIDDEN_KEY, nextHidden ? 'yes' : 'no');
    } catch (storageProblem) {
      // storage blocked: the choice lasts until the page reloads
    }

    setIsHidden(nextHidden);
  }

  return (
    <div className="cb-zr-card cb-zr-details" id="cb-rel-details">
      <button type="button" className="cb-zr-details-toggle" aria-expanded={isHidden ? 'false' : 'true'} onClick={flipDetails}>
        {isHidden ? 'Show Details' : 'Hide Details'}
      </button>
      {isHidden ? null : sections.map((oneSection) => (
        <div key={oneSection.title} className="cb-zr-section">
          <h2 className="cb-zr-section-title">{sentenceCaseTitle(oneSection.title)}</h2>
          <div className="cb-zr-labelleft">
            <SectionFacts record={record} section={oneSection} onSaveField={onSaveField} />
          </div>
        </div>
      ))}
    </div>
  );
}
